import type { ChainRow, ChainSection } from "./optionsChain.types";

/** Shared row height for strike, spot, and band rows so the three panes stay aligned. */
export const ROW_HEIGHT = 28;
export const GROUP_HEADER_HEIGHT = 24;
export const COLUMN_HEADER_HEIGHT = 26;
export const HEADER_HEIGHT = GROUP_HEADER_HEIGHT + COLUMN_HEADER_HEIGHT;

const SECTION_OVERSCAN_ROWS = 6;

export type VisualRow =
  | { kind: "spot"; key: string }
  | {
      kind: "strike";
      key: string;
      row: ChainRow;
      strikeIndex: number;
      expirationLabel: string;
    };

export interface VisualSection {
  expiration: string;
  label: string;
  isCollapsed: boolean;
  withBand: boolean;
  rows: VisualRow[];
  start: number;
  length: number;
}

/** Inclusive section indexes overlapping the viewport; `startIndex` is -1 when none do. */
export interface SectionRange {
  startIndex: number;
  endIndex: number;
}

/** Insertion index for the spot marker: after every strike below the price. */
const spotRowIndex = (rows: readonly ChainRow[], spot: number) => {
  const above = rows.findIndex((row) => row.strike > spot);
  return above === -1 ? rows.length : above;
};

/**
 * Flatten stacked expirations into uniformly-high visual rows.
 * Bands exist only when more than one expiration is on screen.
 */
export const toVisualSections = (
  sections: readonly ChainSection[],
  collapsed: ReadonlySet<string>,
  spot: number | undefined,
): VisualSection[] => {
  const withBand = sections.length > 1;
  let strikeIndex = 0;
  let start = 0;

  return sections.map((section) => {
    const isCollapsed = withBand && collapsed.has(section.expiration);
    const rows: VisualRow[] = [];

    if (!isCollapsed) {
      const spotAt = spot === undefined ? -1 : spotRowIndex(section.rows, spot);
      section.rows.forEach((row, index) => {
        if (index === spotAt) {
          rows.push({ kind: "spot", key: `spot-${section.expiration}` });
        }
        rows.push({
          kind: "strike",
          key: `${section.expiration}-${row.strike}`,
          row,
          strikeIndex,
          expirationLabel: section.label,
        });
        strikeIndex += 1;
      });
      if (spotAt === section.rows.length) {
        rows.push({ kind: "spot", key: `spot-${section.expiration}` });
      }
    }

    const length = rows.length + (withBand ? 1 : 0);
    const visual: VisualSection = {
      expiration: section.expiration,
      label: section.label,
      isCollapsed,
      withBand,
      rows,
      start,
      length,
    };
    start += length;
    return visual;
  });
};

/** Which sections overlap the viewport, including overscan. */
export const sectionRange = (
  sections: readonly VisualSection[],
  scrollTop: number,
  viewportHeight: number,
): SectionRange => {
  const first = scrollTop / ROW_HEIGHT - SECTION_OVERSCAN_ROWS;
  const last =
    (scrollTop + viewportHeight) / ROW_HEIGHT + SECTION_OVERSCAN_ROWS;
  let startIndex = -1;
  let endIndex = -1;
  for (const [index, section] of sections.entries()) {
    if (section.start + section.length > first && section.start < last) {
      if (startIndex === -1) {
        startIndex = index;
      }
      endIndex = index;
    }
  }
  return { startIndex, endIndex };
};

export const windowSections = (
  sections: readonly VisualSection[],
  range: SectionRange,
) => {
  const visible =
    range.startIndex === -1
      ? []
      : sections.slice(range.startIndex, range.endIndex + 1);
  const total = sections.reduce((rows, section) => rows + section.length, 0);
  const leading = visible[0]?.start ?? total;
  const lastVisible = visible.at(-1);
  const trailing = lastVisible
    ? total - (lastVisible.start + lastVisible.length)
    : 0;
  return { visible, leadingRows: leading, trailingRows: trailing, total };
};

/**
 * Scroll offset of a strike in the shared scroller.
 * Counts expiration bands and spot markers, not just strike rows.
 */
export const scrollTopForStrike = (
  sections: readonly VisualSection[],
  strikeIndex: number,
): number => {
  for (const section of sections) {
    const band = section.withBand ? 1 : 0;
    for (const [index, item] of section.rows.entries()) {
      if (item.kind === "strike" && item.strikeIndex === strikeIndex) {
        return (section.start + band + index) * ROW_HEIGHT;
      }
    }
  }
  return strikeIndex * ROW_HEIGHT;
};
